import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createExploreSection,
  createExploreSubSection,
  deleteExploreSection,
  deleteExploreSubSection,
  getExploreSection,
  getExploreSections,
  getSectionImageUploadUrl,
  getSubSectionImageUploadUrl,
  updateExploreSection,
  updateExploreSubSection,
} from "@/api";
import type { ExploreSection, ExploreSubSection } from "@/types";

// Hook for managing Explore sections and subsections
// Provides CRUD operations for admins to manage blog/research articles
export const useExploreSections = () => {
  const queryClient = useQueryClient();

  // Fetch all explore sections with their subsections
  const useGetAllExploreSections = () => {
    return useQuery({
      queryKey: ["exploreSections"],
      queryFn: async () => {
        const response = await getExploreSections();
        return response.data;
      },
    });
  };

  // Fetch a single explore section with its subsections
  const useGetExploreSection = (sectionId: string) => {
    return useQuery({
      queryKey: ["exploreSections", sectionId],
      queryFn: async () => {
        const response = await getExploreSection(sectionId);
        return response.data;
      },
      enabled: !!sectionId,
    });
  };

  // Create a new explore section
  const useCreateExploreSection = () => {
    return useMutation({
      mutationFn: async (
        data: Partial<ExploreSection> & { image_extension?: string },
      ) => {
        const response = await createExploreSection(data);
        return response.data;
      },
      onSuccess: () => {
        // Invalidate cache so the list refreshes
        void queryClient.invalidateQueries({ queryKey: ["exploreSections"] });
      },
    });
  };

  // Update an existing explore section
  const useUpdateExploreSection = () => {
    return useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: Partial<ExploreSection> & { image_extension?: string };
      }) => {
        const response = await updateExploreSection(id, data);
        return response.data;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["exploreSections"] });
      },
    });
  };

  // Delete an explore section
  const useDeleteExploreSection = () => {
    return useMutation({
      mutationFn: async (id: string) => {
        await deleteExploreSection(id);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["exploreSections"] });
      },
    });
  };

  // Create a new subsection (article) within a section
  const useCreateExploreSubSection = () => {
    return useMutation({
      mutationFn: async (
        data: Partial<ExploreSubSection> & { image_extension?: string },
      ) => {
        const response = await createExploreSubSection(data);
        return response.data;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["exploreSections"] });
      },
    });
  };

  // Update an existing subsection
  const useUpdateExploreSubSection = () => {
    return useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: Partial<ExploreSubSection> & { image_extension?: string };
      }) => {
        const response = await updateExploreSubSection(id, data);
        return response.data;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["exploreSections"] });
      },
    });
  };

  // Delete a subsection
  const useDeleteExploreSubSection = () => {
    return useMutation({
      mutationFn: async (id: string) => {
        await deleteExploreSubSection(id);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["exploreSections"] });
      },
    });
  };

  // Get upload URL for section image
  const useGetSectionImageUploadUrl = () => {
    return useMutation({
      mutationFn: async ({
        sectionId,
        fileName,
      }: {
        sectionId: string;
        fileName: string;
      }) => {
        const response = await getSectionImageUploadUrl(sectionId, fileName);
        return response.data;
      },
    });
  };

  // Get upload URL for subsection image
  const useGetSubSectionImageUploadUrl = () => {
    return useMutation({
      mutationFn: async ({
        subSectionId,
        fileName,
      }: {
        subSectionId: string;
        fileName: string;
      }) => {
        const response = await getSubSectionImageUploadUrl(
          subSectionId,
          fileName,
        );
        return response.data;
      },
    });
  };

  return {
    useGetAllExploreSections,
    useGetExploreSection,
    useCreateExploreSection,
    useUpdateExploreSection,
    useDeleteExploreSection,
    useCreateExploreSubSection,
    useUpdateExploreSubSection,
    useDeleteExploreSubSection,
    useGetSectionImageUploadUrl,
    useGetSubSectionImageUploadUrl,
  };
};
